import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Star,
  TrendingUp,
  TrendingDown,
  Minus,
  Clock,
  User,
} from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { useFormatting } from '../../hooks/useFormatting';
import { ComprehensiveScenario } from '../../types/scenarios';
import { cn } from '../../lib/utils';
import { ImpactHistoryIcon } from './ImpactHistoryIcon';
import { useTerminology } from '../../hooks/useTerminology';
import { ScenarioWorkflowActions, ScenarioStatusBadge } from './ScenarioWorkflowActions';
import { ScenarioActionsMenu } from './ScenarioActionsMenu';
import { categoryLabels, resolveCategory } from './scenarioCategory';

interface ScenarioCardProps {
  scenario: ComprehensiveScenario;
  onSelect: (id: string) => void;
  onEdit?: (id: string) => void;
  onCompare?: (id: string) => void;
  onClone?: (id: string) => void;
}

export const ScenarioCard: React.FC<ScenarioCardProps> = ({
  scenario,
  onSelect,
  onClone,
}) => {
  // Base Forecast must never appear as a regular ScenarioCard — defensive guard
  if (scenario.id === 'base') return null;

  const { formatCurrency } = useFormatting();

  const { cashDurationShortTerm } = useTerminology();
  const metricLabel = cashDurationShortTerm.charAt(0).toUpperCase() + cashDurationShortTerm.slice(1);

  // Net-positive cash flow: runway is Infinity, non-finite, or null/undefined.
  // Mirror the scenario detail page which shows "Positive Cash Flow" here.
  const isPositiveCashFlow = !Number.isFinite(scenario.metrics.runway);

  // Category is always explicitly set on the scenario by the creator
  const category = resolveCategory(scenario);

  // Category-based styling — borders use --mc-* tokens to match dashboard metric cards exactly
  const categoryStyles = {
    growth: {
      borderVar: 'var(--mc-success-border)',
      badge: 'bg-[var(--accent-success-50)] text-[var(--accent-success-900)] border-[var(--accent-success-500)]',
      icon: 'text-[var(--accent-success-500)]',
      gradient: 'from-[var(--accent-success-50)]/50 to-transparent',
    },
    'cost-cutting': {
      borderVar: 'var(--mc-info-border)',
      badge: 'bg-[var(--accent-info-50)] text-[var(--accent-info-900)] border-[var(--accent-info-500)]',
      icon: 'text-[var(--accent-info-500)]',
      gradient: 'from-[var(--accent-info-50)]/50 to-transparent',
    },
    crisis: {
      borderVar: 'var(--mc-error-border)',
      badge: 'bg-[var(--accent-destructive-50)] text-[var(--accent-destructive-900)] border-[var(--accent-destructive-500)]',
      icon: 'text-[var(--accent-destructive-500)]',
      gradient: 'from-[var(--accent-destructive-50)]/50 to-transparent',
    },
    custom: {
      borderVar: 'var(--mc-warning-border)',
      badge: 'bg-[var(--accent-warning-50)] text-[var(--accent-warning-900)] border-[var(--accent-warning-500)]',
      icon: 'text-[var(--accent-warning-500)]',
      gradient: 'from-[var(--accent-warning-50)]/50 to-transparent',
    },
  };

  const style = categoryStyles[category];

  return (
    <>
      <Card
        className="group relative overflow-hidden"
        style={{ borderLeft: `3px solid ${style.borderVar}` }}
      >

        
        {/* Freshness badge + dropdown menu — top-right, badge sits left of the 3-dot menu.
            Outside the clickable area so neither triggers onSelect. */}
        <ScenarioActionsMenu
          scenario={scenario}
          onClone={onClone}
          showFreshnessBadge
          className="absolute top-3 right-3 z-30"
        />

        {/* Clickable content area */}
        <CardContent className="relative p-6 cursor-pointer" onClick={() => onSelect(scenario.id)}>
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0 pr-8">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className={cn('font-medium', style.badge)}>
                  {categoryLabels[category]}
                </Badge>
                {scenario.isFavorite && (
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                )}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-1 truncate">
                {scenario.name}
              </h3>
              <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                {scenario.description}
              </p>
            </div>
          </div>

          {/* Net Cash Impact */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {scenario.metrics.netImpact > 0 ? (
                  <TrendingUp className={cn('h-5 w-5', style.icon)} />
                ) : scenario.metrics.netImpact < 0 ? (
                  <TrendingDown className="h-5 w-5 text-[var(--accent-destructive-500)]" />
                ) : (
                  <Minus className="h-5 w-5 text-gray-400" />
                )}
                <span className="text-xs text-gray-500">Net Cash Impact</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={cn('text-sm font-semibold', scenario.metrics.netImpact > 0 ? 'text-[var(--accent-success-700)]' : scenario.metrics.netImpact < 0 ? 'text-[var(--accent-destructive-700)]' : 'text-gray-700')}>
                  {scenario.metrics.netImpact > 0 ? '+' : ''}{formatCurrency(scenario.metrics.netImpact)}
                </span>
                {scenario.impactHistory && scenario.impactHistory.length > 0 && (
                  <ImpactHistoryIcon
                    history={scenario.impactHistory}
                    title={`Impact History — ${scenario.name}`}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Metrics Grid */}
          <TooltipProvider>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-xs text-gray-500 mb-1 cursor-default underline decoration-dotted">Min Cash</div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    The lowest projected cash balance during the scenario period. Shows the cash floor you need to plan around.
                  </TooltipContent>
                </Tooltip>
                <div className="text-sm font-semibold text-gray-900">
                  {formatCurrency(scenario.metrics.minCash)}
                </div>
              </div>
              <div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-xs text-gray-500 mb-1 cursor-default underline decoration-dotted">{metricLabel}</div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    How many months your cash position is projected to last under this scenario, based on your current burn rate.
                  </TooltipContent>
                </Tooltip>
                {isPositiveCashFlow ? (
                  <div className="text-sm font-semibold" style={{ color: 'var(--mc-success-icon)' }}>
                    Positive Cash Flow
                  </div>
                ) : (
                  <div className="text-sm font-semibold text-gray-900">
                    {scenario.metrics.runway} mo
                  </div>
                )}
              </div>
              <div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-xs text-gray-500 mb-1 cursor-default underline decoration-dotted">Adjustments</div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    The number of individual adjustments (revenue, cost, or timing changes) applied in this scenario.
                  </TooltipContent>
                </Tooltip>
                <div className="text-sm font-semibold text-gray-900">
                  {scenario.adjustmentCount ?? scenario.changes.length}
                </div>
              </div>
            </div>
          </TooltipProvider>

          {/* Footer */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <User className="h-3 w-3" />
                <span>{scenario.createdByName}</span>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1 cursor-default">
                      <Clock className="h-3 w-3" />
                      <span>{formatDate(scenario.modifiedAt)}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Last updated {formatDate(scenario.modifiedAt)}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {/* Status Badge */}
            <ScenarioStatusBadge status={scenario.status} />
          </div>

        </CardContent>

        {/* Workflow Actions — rendered outside CardContent so the hover overlay never covers them */}
        <div className="relative z-10 bg-white px-6 pb-6 pt-3 border-t border-gray-100">
          <ScenarioWorkflowActions scenario={scenario} variant="card" />
        </div>
      </Card>
    </>
  );
};
